import { ThemeColors } from "@/config/colors";
import { CheckoutForm } from "@/features/subscription/components/CheckoutForm";
import { usePremiumStatus } from "@/features/subscription/hooks/usePremiumStatus";
import { auth, functions } from "@/libs/firebase";
import { caremeTheme } from "@/libs/theme";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { Container } from "@mui/system";
import { Elements } from "@stripe/react-stripe-js";
import { type StripeElementsOptions, loadStripe } from "@stripe/stripe-js";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useHttpsCallable } from "react-firebase-hooks/functions";

const stripePromise = loadStripe(
	// biome-ignore lint/style/noNonNullAssertion: <explanation>
	process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!,
);

type Plan = {
	id: string;
	rcId: "$rc_monthly" | "$rc_annual" | "$rc_six_month";
	name: string;
	priceId: string;
	priceAmount: number;
};

const useSettingIntent = () => {
	const [clientSecret, setClientsSecret] = useState<string>("");

	const [createSetupIntentFunction] = useHttpsCallable<
		undefined,
		{ clientSecret: string; paymentMethodId: string }
	>(functions, "createSetupIntent");

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		createSetupIntentFunction().then((d) => {
			if (d?.data) {
				setClientsSecret(d.data.clientSecret);
			}
		});
	}, []);

	return clientSecret;
};

export function Purchase() {
	const { enqueueSnackbar } = useSnackbar();
	const [plans, setPlans] = useState<Plan[]>([]);
	const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
	const [user] = useAuthState(auth);
	const { isPremium, platform } = usePremiumStatus(user?.uid);
	const router = useRouter();

	const [fetchAvailablePlanList, loading] = useHttpsCallable<
		{ env: string },
		Plan[]
	>(functions, "availablePlanList");

	useEffect(() => {
		if (plans.length > 0) return;

		fetchAvailablePlanList({
			env: "production",
		}).then((d) => {
			if (d?.data) setPlans(d.data);
		});
	}, [fetchAvailablePlanList, plans.length]);

	useEffect(() => {
		if (plans.length > 0) {
			setSelectedPlan(plans[1] || plans[0]);
		}
	}, [plans]);

	useEffect(() => {
		if (isPremium && platform === "stripe") {
			router.replace("/premium");

			enqueueSnackbar("すでにプランを契約済みです", {
				variant: "info",
				anchorOrigin: {
					vertical: "top",
					horizontal: "center",
				},
			});
		}
	}, [enqueueSnackbar, isPremium, platform, router]);

	const clientSecret = useSettingIntent();

	const options: StripeElementsOptions = {
		clientSecret,
	};

	if (loading) {
		return (
			<>
				<Container component="header" maxWidth="xs" style={{ padding: 0 }}>
					<Box
						px={2}
						height={64}
						display="flex"
						alignItems="center"
						justifyContent="space-between"
						bgcolor={ThemeColors[0].colors.primary[500]}
					>
						<Image
							src="/logo-white.png"
							width={130}
							height={27}
							alt="ケアミーのロゴ"
						/>
					</Box>
				</Container>

				<Container component="main" maxWidth="xs" style={{ padding: 0 }}>
					<Box
						height={"300px"}
						display="flex"
						justifyContent="center"
						alignItems="center"
					>
						<CircularProgress color="primary" />
					</Box>
				</Container>
			</>
		);
	}

	return (
		<>
			<Container component="header" maxWidth="xs" style={{ padding: 0 }}>
				<Box
					px={2}
					height={64}
					display="flex"
					alignItems="center"
					justifyContent="space-between"
					bgcolor={ThemeColors[0].colors.primary[500]}
				>
					<Image
						src="/logo-white.png"
						width={130}
						height={27}
						alt="ケアミーのロゴ"
					/>
				</Box>
			</Container>

			<Container component="main" maxWidth="xs" style={{ padding: 0 }}>
				<Box px={3} pt={6} pb={5} bgcolor={ThemeColors[0].colors.primary[500]}>
					<Typography
						variant="h5"
						component="h1"
						fontWeight="700"
						textAlign="center"
					>
						ケアミープレミアムの購入
					</Typography>
					<Typography variant="body1" textAlign="center" mt={3}>
						ケアミープレミアムはいつでも解約できます
					</Typography>
				</Box>

				<Box
					display="flex"
					flexDirection={"column"}
					alignItems="center"
					mt={-2}
					px={3}
					sx={{
						backgroundImage: "url(../premium/rounded-background.png)",
						backgroundRepeat: "no-repeat",
						backgroundPositionY: "top",
						backgroundSize: "cover",
					}}
				>
					<Box width="100%" mt={8}>
						{plans.length === 0 && (
							<Box
								height={"300px"}
								display="flex"
								justifyContent="center"
								alignItems="center"
							>
								<CircularProgress color="primary" />
							</Box>
						)}

						<Stack spacing={2}>
							{plans.map((plan) => (
								<Box
									px={2.25}
									py={2.5}
									sx={{ flexGrow: 1 }}
									border={selectedPlan?.id === plan.id ? 2 : 1}
									borderRadius={3.5}
									borderColor={
										selectedPlan?.id === plan.id
											? caremeTheme.palette.primary.main
											: caremeTheme.palette.grey[300]
									}
									key={plan.name}
									bgcolor={
										selectedPlan?.id === plan.id
											? `${caremeTheme.palette.primary.main}26`
											: "transparent"
									}
									onClick={() => {
										setSelectedPlan(plan);
									}}
								>
									<Grid container spacing={2} alignItems="center">
										<Grid xs={8}>
											<Typography fontWeight="700">{plan.name}</Typography>
										</Grid>
										<Grid xs={4}>
											<Typography
												textAlign="right"
												variant="h5"
												color={caremeTheme.palette.primary.main}
											>
												{`¥${plan.priceAmount}`}
												<Typography
													variant="body2"
													component="span"
													color={caremeTheme.palette.primary.main}
												>
													{" / 月"}
												</Typography>
											</Typography>
										</Grid>
									</Grid>
								</Box>
							))}
						</Stack>
					</Box>

					{clientSecret && selectedPlan ? (
						<Box width="100%" mt={8}>
							<Elements options={options} stripe={stripePromise}>
								<CheckoutForm
									clientSecret={clientSecret}
									priceId={selectedPlan.priceId}
									buttonText="1週間無料でおためしする"
								/>
							</Elements>
							<Typography
								variant={"caption"}
								component={"p"}
								textAlign="center"
							>
								ケアミープレミアムはいつでも解約できます
							</Typography>

							<Typography variant={"caption"} component={"p"} mt={8} mb={8}>
								※必ず購入前に
								<Link
									href={"/terms"}
									target="_blank"
									style={{ textDecoration: "none", color: "inherit" }}
								>
									<Typography variant="caption" component={"span"}>
										利用規約
									</Typography>
								</Link>
								と
								<Link
									href={"/privacy-policy"}
									target="_blank"
									style={{ textDecoration: "none", color: "inherit" }}
								>
									<Typography variant="caption" component={"span"}>
										プライバシーポリシー
									</Typography>
								</Link>
								をご確認ください
								<br />
								※無料おためし期間終了後、次の購読期間の支払いが自動的に開始されます
								<br />
								※過去におためし経験のある方は、無料おためし期間が適用されることなく所定の料金が発生します
							</Typography>
						</Box>
					) : (
						<Box py={10}>
							<CircularProgress color="primary" />
						</Box>
					)}
				</Box>
			</Container>
		</>
	);
}
